<template>
  <v-simple-table
    id="image-list-ui"
    ref="imageListContainer"
    width="100%"
    class="image-list-container"
  >
    <template slot="default">
      <thead>
        <tr>
          <th class="text-left">
            {{ $t('common.images') }}
          </th>
          <th class="text-left">
            {{ $t('common.fileName') }}
          </th>
        </tr>
      </thead>
      <tbody v-if="!getLoading">
        <tr
          v-for="(image, index) in images"
          :ref="'image-row-' + index"
          :key="image.id"
          :class="{
            hovered: index === hoveredIndex,
            selected: isImageSelected(image.id),
          }"
          tabindex="0"
          class="image-row"
          @keydown.space.prevent="handleImageSelect(image, index)"
          @keydown.enter.prevent="handleImageEdit(image, index)"
          @keydown.up.prevent="handleMoveUp(image, index)"
          @keydown.down.prevent="handleMoveDown(image, index)"
          @click="handleImageSelect(image, index, $event)"
          @mouseenter="handleMouseEnter(index)"
          @mouseleave="handleMouseLeave"
        >
          <td
            style="padding: 3px 0px 0px 8px"
            block
            x-large
            tile
          >
            <div style="position: relative;">
              <v-img
                class="ma-0 border-md"
                max-width="140px"
                height="100px"
                :src="image.thumbImgUrl"
                :alt="image.imgName"
                contain
                :options="{ rootMargin: '240px 0px' }"
              />
            </div>
          </td>
          <td>
            <div class="d-flex align-center">
                <span>{{ image.imgName }}</span>
              <v-chip
                v-if="isImageSelected(image.id)"
                small
                color="primary"
                outlined
                class="selection-chip"
              >
                <v-icon left small>
                  mdi-check-circle
                </v-icon>
                {{ $t('common.selectedBadge') }}
              </v-chip>
            </div>
          </td>
        </tr>
      </tbody>
      <tbody v-else-if="getLoading">
        <tr v-for="i in 8" :key="`skeleton-${i}`" class="image-row">
          <td style="padding: 3px 0px 0px 8px">
            <v-skeleton-loader
              class="ma-0 border-md"
              max-width="140px"
              max-height="100px"
              width="100%"
              height="100px"
              type="image"
            />
          </td>
          <td>
            <div class="d-flex align-center">
              <v-skeleton-loader
                max-width="200px"
                width="80%"
                height="20px"
                type="text"
              />
            </div>
          </td>
        </tr>
      </tbody>
    </template>
  </v-simple-table>
</template>

<script>
import { mapGetters } from 'vuex';

export default {
  name: 'ImageListUI',

  props: {
    // 이미지 목록
    images: {
      type: Array,
      default: () => []
    },
    // 선택된 이미지 인덱스
    selectedIndex: {
      type: Number,
      default: -1
    },
    // 호버된 이미지 인덱스
    hoveredIndex: {
      type: Number,
      default: -1
    }
    // selectedImages는 store에서 가져오므로 props에서 제거
  },

  computed: {
    ...mapGetters({
      getLoading: 'imageAlignment/getLoading',
      getSelectedImages: 'imageAlignment/getSelectedImages'
    })
  },

  watch: {
    // 선택된 인덱스가 변경되면 해당 행에 포커스 설정
    selectedIndex(newIndex) {
      if (newIndex >= 0 && newIndex < this.images.length) {
        this.$nextTick(() => {
          this.setFocus(newIndex);
        });
      }
    }
  },

  methods: {
    // 이미지 선택 여부 확인
    isImageSelected(imageId) {
      return this.getSelectedImages.some(img => img.id === imageId);
    },
    // 이미지 선택 (토글 방식)
    handleImageSelect(image, index, event) {
      //현재 shift 키 누르고 있는지 확인
      const isShiftKeyPressed = event.shiftKey;
      const isCurrentlySelected = this.isImageSelected(image.id);

      if (isShiftKeyPressed) {
        this.$emit('image-multi-select', { image, index }, event);
      } else {
        // 토글 모드
        this.$emit('image-toggle', {
          image,
          index,
          isSelected: isCurrentlySelected
        }, event);
      }
    },

    // 이미지 편집 (연필 아이콘 클릭)
    handleImageEdit(image, index) {
      this.$emit('image-edit', { image, index });
    },

    // 위로 이동
    handleMoveUp(image, index) {
      this.$emit('move-up', { image, index });
    },

    // 아래로 이동
    handleMoveDown(image, index) {
      this.$emit('move-down', { image, index });
    },

    // 마우스 진입
    handleMouseEnter(index) {
      this.$emit('mouse-enter', index);
    },

    // 마우스 이탈
    handleMouseLeave() {
      this.$emit('mouse-leave');
    },

    // 특정 행에 포커스 설정
    setFocus(index) {
      if (this.$refs[`image-row-${index}`] && this.$refs[`image-row-${index}`][0]) {
        this.$refs[`image-row-${index}`][0].focus();
      }
    }
  }
};
</script>

<style scoped>
.image-row #mdi-pencil {
  color: var(--v-secondary-base);
}
#image-list-ui .v-data-table__wrapper {
  width: 100%;
  height: 100%;
  overflow-y: auto;
}

#image-list-ui table {
  table-layout: fixed;
}

/* 선택 상태 칩 스타일 */
.selection-chip {
  margin-left: 8px;
}
.image-list-container{
  height: calc(100vh - var(--footerHeight) - 2*var(--toolbarHeight) -2*var(--tableHeaderHeight));
  overflow-y: auto;
}
</style>

<style>
/* 선택 행: main.css --solvit-row-selected-on-surface 와 동기 */
.v-application.theme--light #image-list-ui table tr.image-row.selected {
  background-color: var(--solvit-row-selected-on-surface);
}
.v-application.theme--dark #image-list-ui table tr.image-row.selected {
  background-color: var(--solvit-row-selected-on-surface);
}
.v-application.theme--light #image-list-ui table tr.hovered:not(.selected) {
  background-color: var(--v-surface-base);
}
</style>
